// Geboortevenster · Gedeelde staat tussen chartpagina en paneel: chart, venster, data, kandidaat, preview, handlers (patroon Just Now / Locks & Keys).
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { Injectable, computed, signal } from '@angular/core';
import { HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import { HdChannelListGateInfoRequest } from '../bodygraph/channel-list/hd-channel-list-item.component';
import { HdBirthWindowData } from './birth-window.models';
import { HdBirthWindowOverlay } from './birth-window-moment';

/** Vensterbreedtes (halve breedte in minuten) die de toggle aanbiedt; default ±3 u (besluit 6). */
export const HD_BIRTH_WINDOW_HALF_MINUTES = [60, 180, 720] as const;
export type HdBirthWindowHalfMinutes = (typeof HD_BIRTH_WINDOW_HALF_MINUTES)[number];
export const HD_BIRTH_WINDOW_DEFAULT_HALF_MINUTES: HdBirthWindowHalfMinutes = 180;

export interface HdBirthWindowRequestHandlers {
  /** Toon de chart zoals ze op dit moment zou zijn; `undefined` = terug naar de bewaarde chart. */
  readonly previewAt: (ms: number | undefined) => void;
  /** Neem het moment over als nieuwe geboortetijd (bewerkpad, besluit 4). */
  readonly commit: (ms: number) => Promise<void>;
  readonly openGateInfo?: (request: HdChannelListGateInfoRequest) => void;
}

/**
 * Gedeelde staat van het geboortevenster-paneel. De chart-pagina bezit de
 * chart en de preview; ze spiegelt hierheen wat het gedockte paneel nodig
 * heeft en registreert de handlers. Zelfde patroon als
 * `HdJustNowPanelStateService` / `HdLocksKeysStateService`.
 */
@Injectable({ providedIn: 'root' })
export class HdBirthWindowStateService {
  readonly preset = signal<HdBodygraphChartPreset | undefined>(undefined);
  readonly savedChartId = signal<string | undefined>(undefined);
  readonly chartName = signal('');
  /** Chart van een moment (Just Now, Rave New Year): teksten over het tijdstip i.p.v. de geboorte. */
  readonly momentMode = signal(false);
  /**
   * Bewaard moment: de natal chart als vaste laag (enkel het tijdstip van het
   * moment schuift, zie `birth-window-moment.ts`); undefined = gewone chart.
   */
  readonly overlay = signal<HdBirthWindowOverlay | undefined>(undefined);
  readonly halfWindowMinutes = signal<HdBirthWindowHalfMinutes>(HD_BIRTH_WINDOW_DEFAULT_HALF_MINUTES);
  /** Exacte vensterdata (backend) voor de actieve preset + breedte. */
  readonly data = signal<HdBirthWindowData | undefined>(undefined);
  /** Eerste-orde-schatting (indicator), ook gebruikt voor "eerste wijziging buiten het venster". */
  readonly estimate = signal<HdBirthWindowData | undefined>(undefined);
  readonly loading = signal(false);
  readonly error = signal<string | undefined>(undefined);
  /** Kandidaat-geboortemoment (ms); `undefined` = op de geboorte zelf. */
  readonly candidateMs = signal<number | undefined>(undefined);
  readonly committing = signal(false);
  /** Zwevend paneel smal (±62vw) i.p.v. breed (±90vw), zoals het transits-paneel. */
  readonly panelNarrow = signal(false);
  /**
   * Chart op de kandidaattijd (of undefined = de geboortechart). De pagina vult
   * dit (direct uit de segmenten, exact op rust); het paneel toont de chart
   * zelf, de hoofdchart volgt niet mee (feedbackronde 1).
   */
  readonly previewPreset = signal<HdBodygraphChartPreset | undefined>(undefined);
  /** Planetenlaag uitgeklapt (13 lanes met P/D-spoor) of ingeklapt (één markerlane). */
  readonly planetsExpanded = signal(false);
  /** Chart in het paneel tonen (uit te zetten in een krappe dock). */
  readonly chartVisible = signal(true);
  readonly requestHandlers = signal<HdBirthWindowRequestHandlers | undefined>(undefined);

  readonly birthUtc = computed(() => {
    const birth = this.preset()?.birthData as { time?: { utc?: string | null } } | undefined;
    return birth?.time?.utc ?? undefined;
  });
  readonly birthMs = computed(() => {
    const utc = this.birthUtc();
    const ms = utc ? Date.parse(utc) : Number.NaN;
    return Number.isFinite(ms) ? ms : undefined;
  });
  readonly timeZoneId = computed(() => {
    const birth = this.preset()?.birthData as { time?: { timezone?: { id?: string | null } | null } } | undefined;
    return birth?.time?.timezone?.id ?? undefined;
  });

  clear(): void {
    this.preset.set(undefined);
    this.savedChartId.set(undefined);
    this.chartName.set('');
    this.momentMode.set(false);
    this.overlay.set(undefined);
    this.data.set(undefined);
    this.estimate.set(undefined);
    this.loading.set(false);
    this.error.set(undefined);
    this.candidateMs.set(undefined);
    this.committing.set(false);
    this.previewPreset.set(undefined);
  }
}
